import { ChangeEvent, ReactNode } from "react";
import moment from "moment";
import { Box, Button, Skeleton } from "@mui/material";
import strings from "global/constants/StringConstants";
import { fillCopy } from "helpers/methods";
import styles from "../AiWebForm.styles";
import { messagesCount } from "../AiWebFormHelper";
import { AiSessionSummary, MAX_PROMPT_LENGTH } from "../AiWebFormTypesAndValidation";
import { PencilGlyph, SparkleGlyph } from "./AiWebFormIcons";

const copy = strings.aiWebForm;

export type BuildChoice = "ai" | "unlayer";

interface Props {
  /** The AI design this template signer already has, shown first (01b). */
  existing: AiSessionSummary | null;
  /** Looking for an existing design. */
  checking: boolean;
  choice: BuildChoice;
  onChoice: (choice: BuildChoice) => void;
  prompt: string;
  onPrompt: (prompt: string) => void;
  onContinueExisting: (session: AiSessionSummary) => void;
  onRemoveExisting: (session: AiSessionSummary) => void;
  onStartNew: () => void;
  onWithoutAi: () => void;
  removing: boolean;
}

const ChoiceCard = ({
  selected,
  brand,
  icon,
  title,
  badge,
  description,
  onClick,
  radio = true,
}: {
  selected: boolean;
  brand: boolean;
  icon: ReactNode;
  title: string;
  badge?: string;
  description: string;
  onClick: () => void;
  /** A choice in a radio group (01a); false where the card acts at once (01b). */
  radio?: boolean;
}) => (
  // Spans only inside the button: a <button> takes phrasing content.
  <Box
    component="button"
    type="button"
    role={radio ? "radio" : undefined}
    aria-checked={radio ? selected : undefined}
    sx={styles.choiceCard(selected)}
    onClick={onClick}
  >
    <Box component="span" sx={styles.radioMark(selected)} aria-hidden="true" />
    <Box component="span" sx={styles.choiceTile(brand)} aria-hidden="true">
      {icon}
    </Box>
    <Box component="span" sx={styles.choiceText}>
      <Box component="span" sx={styles.choiceTitle}>
        {title}
        {badge && (
          <Box component="span" sx={styles.newBadge}>
            {badge}
          </Box>
        )}
      </Box>
      <Box component="span" sx={styles.choiceDescription}>
        {description}
      </Box>
    </Box>
  </Box>
);

/** "How do you want to build this form?": Build with AI (with the look described) or the drag-and-drop editor. */
export const AiBuildChoice = ({
  existing,
  checking,
  choice,
  onChoice,
  prompt,
  onPrompt,
  onContinueExisting,
  onRemoveExisting,
  onStartNew,
  onWithoutAi,
  removing,
}: Props) => {
  if (checking) {
    return (
      <Box sx={styles.choiceBody}>
        <Skeleton variant="rounded" height={96} />
        <Skeleton variant="rounded" height={72} />
      </Box>
    );
  }

  if (existing) {
    return (
      <Box sx={styles.choiceBody}>
        <Box sx={styles.continueCard}>
          <Box sx={styles.continueTop}>
            <Box sx={styles.thumb} aria-hidden="true">
              <span />
              <span />
              <span />
              <span />
              <span />
              <span />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Box sx={styles.continueEyebrow}>
                <SparkleGlyph />
                {copy.continueEyebrow}
              </Box>
              <Box sx={styles.continueTitle}>{existing.title}</Box>
              <Box sx={styles.continueMeta}>
                {fillCopy(copy.continueMeta, {
                  version: existing.version,
                  messages: messagesCount(existing.turns),
                  when: moment(existing.updatedAt).fromNow(),
                })}
              </Box>
              {existing.publishedVersion != null && (
                <Box sx={{ pt: "8px" }}>
                  <Box component="span" sx={styles.publishedBadge}>
                    {fillCopy(copy.publishedVersionBadge, { version: existing.publishedVersion })}
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
          <Box sx={styles.continueActions}>
            <Button variant="contained" onClick={() => onContinueExisting(existing)}>
              {copy.continueButton}
            </Button>
            <Box component="button" type="button" sx={styles.removeLink} disabled={removing} onClick={() => onRemoveExisting(existing)}>
              {copy.removeFromList}
            </Box>
          </Box>
        </Box>
        <Box sx={styles.orDivider}>{copy.or}</Box>
        <ChoiceCard
          selected={false}
          brand
          icon={<SparkleGlyph />}
          title={copy.startNew}
          description={copy.startNewBody}
          onClick={onStartNew}
          radio={false}
        />
        <ChoiceCard
          selected={false}
          brand={false}
          icon={<PencilGlyph />}
          title={copy.withoutAi}
          description={copy.withoutAiBody}
          onClick={onWithoutAi}
          radio={false}
        />
      </Box>
    );
  }

  return (
    <Box sx={styles.choiceBody} role="radiogroup" aria-label={copy.choiceTitle}>
      <ChoiceCard
        selected={choice === "ai"}
        brand
        icon={<SparkleGlyph />}
        title={copy.buildWithAi}
        badge={copy.newBadge}
        description={copy.buildWithAiBody}
        onClick={() => onChoice("ai")}
      />
      {choice === "ai" && (
        <Box sx={styles.describe}>
          <Box component="label" htmlFor="aiwf-describe" sx={styles.describeLabel}>
            {copy.describeLabel} <span>{copy.optional}</span>
          </Box>
          <Box
            component="textarea"
            id="aiwf-describe"
            sx={styles.describeInput}
            value={prompt}
            maxLength={MAX_PROMPT_LENGTH}
            placeholder={copy.describePlaceholder}
            onChange={(event: ChangeEvent<HTMLTextAreaElement>) => onPrompt(event.target.value)}
          />
          <Box sx={styles.describeCounter}>
            {fillCopy(copy.counter, { count: prompt.length, max: MAX_PROMPT_LENGTH })}
          </Box>
          <Box sx={styles.describeChips}>
            {copy.describeChips.map((chip) => (
              <Box
                key={chip}
                component="button"
                type="button"
                sx={styles.tryChip}
                onClick={() =>
                  onPrompt((prompt.trim() ? `${prompt.trim()}, ${chip.toLowerCase()}` : chip).slice(0, MAX_PROMPT_LENGTH))
                }
              >
                {chip}
              </Box>
            ))}
          </Box>
        </Box>
      )}
      <ChoiceCard
        selected={choice === "unlayer"}
        brand={false}
        icon={<PencilGlyph />}
        title={copy.withoutAi}
        description={copy.withoutAiBody}
        onClick={() => onChoice("unlayer")}
      />
    </Box>
  );
};
